import React from 'react';
import { Table, Badge, Spin } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faCircleCheck, faCalendarDays } from '@fortawesome/free-solid-svg-icons';
import {
  SectionCard,
  SectionHeader,
  SectionTitleRow,
  SectionTitle,
  SectionContent,
  TableWrapper,
  EmptyWrap,
  EmptyIconWrap,
  EmptyText,
} from './Styles';
import { SECTION_TITLES, TABLE_COLUMNS, STATUS_LABELS, EMPTY_MESSAGES } from './constants';
import type { PaymentItem } from './types';
import MobilePaymentCard from './MobilePaymentCard';

export type PaymentSectionType = 'pending' | 'cleared' | 'upcoming';

interface PaymentTableSectionProps {
  type: PaymentSectionType;
  data: PaymentItem[];
  isMobile: boolean;
  loading?: boolean;
}

const iconMap = {
  pending: faClock,
  cleared: faCircleCheck,
  upcoming: faCalendarDays,
};

const statusColorMap: Record<PaymentSectionType, string> = {
  pending: 'orange',
  cleared: 'green',
  upcoming: 'blue',
};

const statusLabelMap: Record<PaymentSectionType, string> = {
  pending: STATUS_LABELS.PENDING,
  cleared: STATUS_LABELS.CLEARED,
  upcoming: STATUS_LABELS.SCHEDULED,
};

const emptyMessageMap: Record<PaymentSectionType, string> = {
  pending: EMPTY_MESSAGES.NO_PENDING,
  cleared: EMPTY_MESSAGES.NO_CLEARED,
  upcoming: EMPTY_MESSAGES.NO_UPCOMING,
};

const formatAmount = (amount: number | string): string =>
  typeof amount === 'number' ? `$${amount}` : `$${amount}`;

const PaymentTableSection: React.FC<PaymentTableSectionProps> = ({
  type,
  data,
  isMobile,
  loading = false,
}) => {
  const titleKey = type === 'pending' ? 'PENDING' : type === 'cleared' ? 'CLEARED' : 'UPCOMING';
  const title = SECTION_TITLES[titleKey];
  const icon = iconMap[type];
  const color = statusColorMap[type];
  const emptyMsg = emptyMessageMap[type];

  const columns: ColumnsType<PaymentItem> = [
    {
      title: TABLE_COLUMNS.MERCHANT,
      dataIndex: 'merchant',
      key: 'merchant',
      render: (val: string) => val,
    },
    {
      title: TABLE_COLUMNS.AMOUNT,
      dataIndex: 'amount',
      key: 'amount',
      render: (val: number | string) => formatAmount(val),
    },
    {
      title: type === 'cleared' ? TABLE_COLUMNS.PAYMENT_DATE : TABLE_COLUMNS.DUE_DATE,
      dataIndex: type === 'cleared' ? 'paymentDate' : 'dueDate',
      key: type === 'cleared' ? 'paymentDate' : 'dueDate',
      render: (val: string) => val ?? '—',
    },
    {
      title: TABLE_COLUMNS.STATUS,
      key: 'status',
      render: () => (
        <Badge color={color} text={statusLabelMap[type]} />
      ),
    },
  ];

  const hasData = Array.isArray(data) && data.length > 0;

  return (
    <SectionCard>
      <SectionHeader>
        <SectionTitleRow>
          <FontAwesomeIcon icon={icon} color={color} />
          <SectionTitle>{title}</SectionTitle>
        </SectionTitleRow>
        <Badge count={data.length} color={color} />
      </SectionHeader>
      <SectionContent>
        {isMobile ? (
          loading ? (
            <EmptyWrap>
              <Spin size="default" />
            </EmptyWrap>
          ) : hasData ? (
            data.map((payment, index) => (
              <MobilePaymentCard
                key={`${payment.merchant}-${index}`}
                payment={payment}
                type={type}
              />
            ))
          ) : (
            <EmptyWrap>
              <EmptyIconWrap>
                <FontAwesomeIcon icon={icon} />
              </EmptyIconWrap>
              <EmptyText>{emptyMsg}</EmptyText>
            </EmptyWrap>
          )
        ) : (
          <TableWrapper>
            <Table<PaymentItem>
              columns={columns}
              dataSource={hasData ? data.map((item, idx) => ({ ...item, key: `${item.merchant}-${idx}` })) : []}
              pagination={false}
              loading={loading}
              locale={{
                emptyText: (
                  <EmptyWrap>
                    <EmptyIconWrap>
                      <FontAwesomeIcon icon={icon} />
                    </EmptyIconWrap>
                    <EmptyText>{emptyMsg}</EmptyText>
                  </EmptyWrap>
                ),
              }}
            />
          </TableWrapper>
        )}
      </SectionContent>
    </SectionCard>
  );
};

export default React.memo(PaymentTableSection);
